import * as React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { CheckIcon } from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";

interface DropdownMenuProps {
  children: React.ReactNode;
  className?: string;
  menuItems: {
    text: string;
    icon: React.ReactNode;
    type: string;
    url?: string;
    divider?: boolean;
    onClick?: () => void;
    isSelected?: boolean;
    hoverActions?: {
      icon: React.ReactNode;
      onClick: () => void;
      tooltip: string;
    }[];
  }[];
  side?: "bottom" | "right" | "left" | "top";
}

export default function DropdownMenu({
  children,
  menuItems,
  side = "bottom",
  className,
}: DropdownMenuProps) {
  const router = useRouter();
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side={side}
        className={cn(
          "p-spacing-1 rounded-md bg-palette-surface-container w-[260px]",
          className,
        )}
      >
        {menuItems.map((item, index) => (
          <PoopoverItem
            key={(item as any).id || item.text || `item-${index}`}
            text={item.text}
            icon={item.icon}
            url={item.url}
            type={item.type}
            divider={item.divider}
            onClick={() => {
              setOpen(false);
              if (item.onClick) {
                item.onClick();
              }
            }}
            isSelected={item.isSelected}
            hoverActions={item.hoverActions}
          />
        ))}
      </PopoverContent>
    </Popover>
  );

  function ClickableItem({
    url = "#",
    children,
    type,
    onClick,
  }: {
    url: string;
    children: React.ReactNode;
    type: string;
    onClick?: () => void;
  }) {
    const clickAction = () => {
      if (type === "link") {
        router.push(url);
      } else {
        if (onClick) {
          onClick();
        }
      }
    };

    return (
      <button
        type="button"
        className={`w-full text-palette-text-primary flex h-spacing-8 px-spacing-3 gap-spacing-3 bg-palette-grey-0 hover:bg-palette-grey-200 items-center justify-between cursor-pointer focus-visible:outline-hidden rounded-sm group`}
        onClick={clickAction}
      >
        {children}
      </button>
    );
  }

  function PoopoverItem({
    text,
    icon,
    url = "#",
    divider = false,
    type,
    onClick,
    isSelected = false,
    hoverActions = [],
  }: {
    text: string;
    icon: React.ReactNode;
    url?: string;
    divider?: boolean;
    type: string;
    onClick?: () => void;
    isSelected?: boolean;
    hoverActions?: {
      tooltip: string;
      icon: React.ReactNode;
      onClick: () => void;
    }[];
  }) {
    return (
      <>
        {divider && (
          <div className="h-px bg-palette-border-container m-2 px-3" />
        )}
        <ClickableItem url={url} type={type} onClick={onClick}>
          <div className="flex items-center gap-spacing-3">
            {icon}
            <p className="paragraphs-Medium-Medium">{text}</p>
          </div>
          {isSelected && (
            <div className="flex items-center justify-center p-spacing-2">
              <CheckIcon size={16} className="group-hover:hidden" />
            </div>
          )}
          {hoverActions.length > 0 && (
            <div className="hidden group-hover:flex">
              {hoverActions.map((action, idx) => (
                <Tooltip key={`action-${idx}`} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <div
                      className="w-full p-spacing-2 rounded-sm cursor-pointer hover:bg-palette-surface-action-ghost-hovered-and-focused"
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick();
                      }}
                    >
                      {action.icon}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="ml-2" sideOffset={8}>
                    {action.tooltip}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}
        </ClickableItem>
      </>
    );
  }
}
